import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { convertToUpperSnakeCase } from '../../GetLeads/LeadsSearch/LeadsSearchFiltersMenu/helpers';
import { useMyLeadsCRUDActions } from '@mm/state/actions/MyLeadsCRUDActions';
import {
  MyLeadsSearchQuery,
  SubsectionKeys,
} from '@mm/state/interfaces/myLeads';
import { selectMyLeadsSearchOptions } from '@mm/state/selectors/myLeadsSelectors';
import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useContactsCRUDActions } from '@mm/state/actions/ContactsCRUDActions';
import {
  FilterItem,
  FilterParams,
  FILTER_TYPES,
  TagsFilters,
} from '@mm/state/model/AudiencesModel';
import { ObjectUtils } from '@mm/utils';
import { useLocation } from 'react-router-dom';

export const getEnumeratedFilters = (
  params?: FilterParams | TagsFilters,
  withLogic?: boolean,
) => {
  const filterParamsWithValues = params
    ?.filter((f: FilterItem) => f.value?.length)
    .map(({ esType: _esType = null as any, ...filter }) => ({
      ...filter,
    }));

  const paramsToUse = withLogic ? filterParamsWithValues : params;

  return {
    ...(paramsToUse?.length && withLogic ? { logic: '1' } : {}),
    ...paramsToUse?.reduce((acc, filter, i) => {
      acc[i + 1] = filter;
      return acc;
    }, {}),
  };
};

const checkIfSortingIsAllowed = (filterParams: MyLeadsSearchQuery) => {
  const params = filterParams?.filterParams || [];

  return !params.some(p => p.filterType === FILTER_TYPES.variableValues);
};

export const useFilterMyLeads = ({
  filterParams,
  fetchAttributes = false,
  sortOrder,
}: {
  fetchAttributes?: boolean;
  filterParams: MyLeadsSearchQuery;
  sortOrder?: 'asc' | 'desc';
}) => {
  const { retrieveAttributes } = useMyLeadsCRUDActions();
  const { retrieveAll } = useContactsCRUDActions();
  const [busy, setBusy] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [canUseSorting, setCanUseSorting] = useState(
    checkIfSortingIsAllowed(filterParams),
  );

  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const searchOptions = useSelector(selectMyLeadsSearchOptions);
  const searchVariablesFiltered = Object.entries(
    searchOptions.variables || {},
  ).filter(item => item[1].length);
  const searchVariablesList = Object.fromEntries(searchVariablesFiltered);
  const searchAttributes = searchOptions.attributes;
  const businessContactKey = convertToUpperSnakeCase(SubsectionKeys.leadType);

  const location = useLocation<MyLeadsSearchQuery>();
  const forceFetch = Boolean(Object.keys(location.state || {}).length);

  useEffect(() => {
    (async () => {
      if (fetchAttributes) {
        setBusy(true);
        await retrieveAttributes({ skipBusy: true });
        setBusy(false);
      }
    })();
  }, []);

  const debouncedLeadsSearch = useCallback(
    () =>
      debounce(async () => {
        setBusy(true);

        const isExcludingBusinessContacts =
          !!filterParams.query?.excludedVariables?.[businessContactKey];
        const isIncludingBusinessContacts =
          !!filterParams.query?.variables?.[businessContactKey];
        const updatedFilterParams = filterParams;

        if (isExcludingBusinessContacts && isIncludingBusinessContacts) {
          delete updatedFilterParams.query?.excludedVariables?.[
            businessContactKey
          ];
          delete updatedFilterParams.query?.variables?.[businessContactKey];
        }

        for (const key in updatedFilterParams.query) {
          if (!Object.keys(updatedFilterParams.query[key]).length) {
            delete updatedFilterParams.query[key];
          }
        }

        const enumeratedFilterParams = getEnumeratedFilters(
          updatedFilterParams.filterParams,
          true,
        );
        const enumeratedTags = getEnumeratedFilters(
          updatedFilterParams.tagsFilters,
          false,
        );

        const hasFilterParams = !!Object.keys(enumeratedFilterParams).length;

        const hasESFilter = Object.values(
          updatedFilterParams?.query || {},
        )?.length;

        await retrieveAll(
          facebookPageId,
          filterParams.page || 1,
          undefined,
          undefined,
          {
            skipBusy: true,
            audience: {
              ...(hasESFilter && {
                esFilters: ObjectUtils.deepSnakeKeys(
                  updatedFilterParams?.query || {},
                  true,
                ),
              }),
              ...(hasFilterParams && { filterParams: enumeratedFilterParams }),
              ...(updatedFilterParams.tagsFilters?.length && {
                tagsFilters: enumeratedTags,
              }),
            },
            ...(canUseSorting && sortOrder
              ? {
                  sortColumn: 'created_at',
                  sortDirection: sortOrder,
                }
              : {}),
            withDeliverable: true,
          },
        );
        setBusy(false);
      }, 300),
    [retrieveAll, filterParams, businessContactKey, facebookPageId, sortOrder],
  );

  useEffect(() => {
    if (forceFetch) {
      debouncedLeadsSearch()();
    }
  }, []);

  useEffect(() => {
    if (mounted) {
      debouncedLeadsSearch()();
    } else {
      setMounted(true);
    }
  }, [filterParams, debouncedLeadsSearch, sortOrder]);

  useEffect(() => {
    if (filterParams) {
      setCanUseSorting(checkIfSortingIsAllowed(filterParams));
    }
  }, [filterParams]);

  return {
    searchAttributes,
    searchVariablesList,
    busy,
    canUseSorting,
  };
};
